<template>
  <div class="child-route-wrapper">
    <h1 class="registration-form-title">
      Create an account
    </h1>
    <p class="registration-form-subtitle">
      Sign up to create an account and explore many things
    </p>
    <validation-observer
      #default="{ invalid }"
      ref="observer"
      class="registration-form-wrapper"
    >
      <b-form
        class="registration-form"
        @submit.prevent="onFormSubmit"
      >
        <validation-provider
          v-slot="{ errors, touched, valid }"
          ref="emailProvider"
          name="email"
          vid="email"
          rules="required|email"
        >
          <b-form-group
            class="registration-input-group"
            label="Email address"
            label-for="email"
            label-class="registration-label"
          >
            <b-form-input
              id="email"
              v-model="email"
              class="form-control"
              type="email"
              placeholder="Your account email"
              :state="errors.length > 0 && touched ? false : null"
              :class="{ 'is-valid': valid && touched }"
            />
            <div
              v-if="errors.length > 0 && touched"
              class="invalid-feedback d-block input-error"
            >
              {{ errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <div class="terms-checkbox-container mt-2 mb-3">
          <b-form-checkbox
            id="terms-checkbox"
            v-model="termsAccepted"
            name="terms-checkbox"
          >
            Yes, I understand and agree to the Kingpin
            <b-link
              href="https://kingpin.global/terms-and-conditions"
              target="_blank"
              class="cursor-pointer link-style"
              @click="handleTermsLinkClick"
            >
              Terms of Service
            </b-link>
            &
            <b-link
              href="https://kingpin.global/privacy-policy"
              target="_blank"
              class="cursor-pointer link-style"
              @click="handlePrivacyLinkClick"
            >
              Privacy Policy
            </b-link>
          </b-form-checkbox>
        </div>

        <b-button
          class="registration-button"
          type="submit"
          :disabled="invalid || !termsAccepted || isLoading"
        >
          {{ isLoading ? 'Sending...' : 'Sign up' }}
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm, BFormGroup, BFormInput, BButton, BFormCheckbox, BLink,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { AUTH_REGISTER_V2_CHILDREN } from '@/router/routes/utils'
import { useFormFields } from '@/views/apps/authentication-v2/useFormFields'
import analytics from '@core/utils/analytics'
import constants from '@/constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { watch } from '@vue/composition-api'
import { debounce } from 'lodash'
import { REQUEST_OTP, VERIFY_EMAIL } from '@/store/modules/register.module'
import { mapActions } from 'vuex'

export default {
  name: 'SetEmail',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormCheckbox,
    BLink,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      isLoading: false,
    }
  },
  watch: {
    email() {
      // Clear email validation errors when email changes
      const emailProvider = this.$refs.emailProvider
      if (emailProvider && emailProvider.errors.includes('This email is already taken. Please try another one.')) {
        emailProvider.setErrors([])
      }
    },
  },
  mounted() {
    analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_VIEW, {
      pageName: 'Set Email Page',
    })
  },
  setup() {
    const { email, termsAccepted } = useFormFields()

    const trackInputFields = debounce(newEmail => {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_ENTERS_DATA, {
        formData: {
          email: newEmail,
        },
      })
    }, 1000)

    watch(
      email,
      newEmail => {
        trackInputFields(newEmail)
      },
    )

    // Track when user accepts terms and conditions
    watch(
      termsAccepted,
      (newVal, oldVal) => {
        if (newVal && !oldVal) {
          analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_TERMS_CHECK)
        }
      },
    )

    return {
      email,
      termsAccepted,
    }
  },
  methods: {
    ...mapActions({
      requestOtp: REQUEST_OTP,
      verifyEmail: VERIFY_EMAIL,
    }),
    async checkEmailAvailability(email) {
      try {
        const { data } = await this.verifyEmail({ email })
        return this.handleEmailAvailabilityResponse(data?.isAvailable)
      } catch (error) {
        this.handleError(error)
        return false
      }
    },

    handleEmailAvailabilityResponse(isAvailable) {
      const emailProvider = this.$refs.emailProvider

      if (!isAvailable) {
        const errorMsg = 'This email is already taken. Please try another one.'
        emailProvider.setErrors([errorMsg])
        apiToastError(errorMsg)
        return false
      }

      if (emailProvider.errors.length > 0) {
        emailProvider.setErrors([])
      }

      return true
    },

    handleError(error) {
      const message = error.response?.data?.message
        || 'An error occurred checking email availability.'
      apiToastError(message)
    },
    async onSubmit() {
      try {
        this.isLoading = true
        // Track analytics event
        analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_SIGNUP_CLICK, {
          formData: { email: this.email },
        })

        // Check if email is free
        const isEmailAvailable = await this.checkEmailAvailability(this.email)
        if (!isEmailAvailable) {
          this.isLoading = false
          return
        }

        // Request OTP code to the email provided
        await this.requestOtp({ email: this.email })

        // Show success toast
        apiToastSuccess('Verification code sent to your email')

        // After successful OTP request, redirect to OTP component
        await this.$router.push({ name: AUTH_REGISTER_V2_CHILDREN.SET_OTP })
      } catch (error) {
        apiToastError(error.response?.data?.message || 'An error occurred. Please try again.')
      } finally {
        this.isLoading = false
      }
    },
    onFormSubmit() {
      // Prevent form submission if terms aren't accepted or form is invalid
      if (!this.termsAccepted || this.$refs.observer.invalid) {
        return
      }

      this.onSubmit()
    },
    handleTermsLinkClick() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_TOS_CLICK)
    },
    handlePrivacyLinkClick() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_PRIVACY_CLICK)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../common-styles.scss';

.registration-form-title {
  margin-bottom: 12px;
}

.registration-form-subtitle {
  text-align: center;
  margin-bottom: 40px;
  font-size: 16px;
  line-height: 24px;
  color: #6c757d;
}

.registration-form {
  width: 100%;
}

.terms-checkbox-container {
  margin-top: 1rem;
}

.link-style {
  color: #5e5ce6;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
